import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CalendarClock, Plus } from 'lucide-react';
import { adminApi, type AdminOrgUsage, type ApiPlan, type BillingTerm } from '../api';
import { TERM_LABELS, normalizeBillingTerm, getPricingOptions, formatPlanPrice } from '../lib/planPricing';

function formatExpiry(iso: string | null | undefined): string {
  if (!iso) return 'No expiry set';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function daysRemaining(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (!Number.isFinite(end)) return null;
  return Math.ceil((end - Date.now()) / 86400000);
}

function expiryLabel(iso: string | null | undefined): string {
  const days = daysRemaining(iso);
  if (days == null) return 'Lifetime / not set';
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Expires today';
  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

function planRank(plan: ApiPlan) {
  const from = getPricingOptions(plan)[0]?.priceInr ?? plan.priceInr ?? 0;
  return (plan.sortOrder ?? 0) * 100000 + from;
}

type Props = {
  detail: AdminOrgUsage;
  onUpdated: () => Promise<void>;
};

export function TenantSubscriptionExtend({ detail, onUpdated }: Props) {
  const org = detail.organization;
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [planId, setPlanId] = useState(org.planId || '');
  const [changePlanId, setChangePlanId] = useState('');
  const [addMonths, setAddMonths] = useState('1');
  const [billingTerm, setBillingTerm] = useState<BillingTerm>(
    normalizeBillingTerm((org as { billingTerm?: BillingTerm }).billingTerm || 'monthly')
  );
  const [mode, setMode] = useState<'months' | 'term'>('months');
  const [saving, setSaving] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);

  useEffect(() => {
    let done = false;
    void adminApi.listPlans().then((res) => {
      if (!done) {
        setPlans(res.plans.filter((p) => p.isActive).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
      }
    });
    return () => {
      done = true;
    };
  }, []);

  useEffect(() => {
    setPlanId(org.planId || '');
    setChangePlanId('');
    setBillingTerm(normalizeBillingTerm((org as { billingTerm?: string }).billingTerm || 'monthly'));
  }, [org]);

  const currentPlan = useMemo(
    () => detail.plan || plans.find((p) => p.planId === org.planId) || null,
    [detail.plan, plans, org.planId]
  );
  const currentRank = currentPlan ? planRank(currentPlan) : 0;
  const planName = currentPlan?.name || org.planId || '—';
  const termLabel = TERM_LABELS[billingTerm] || billingTerm;
  const expiryTone = useMemo(() => {
    const days = daysRemaining(org.subscriptionExpiresAt);
    if (days == null) return 'text-zinc-600';
    if (days < 0) return 'text-rose-700';
    if (days <= 7) return 'text-amber-800';
    return 'text-emerald-800';
  }, [org.subscriptionExpiresAt]);

  const otherPlans = plans.filter((p) => p.planId !== org.planId);
  const selectedChange = plans.find((p) => p.planId === changePlanId) || null;
  const changeAction: 'upgrade' | 'downgrade' | null = selectedChange
    ? planRank(selectedChange) > currentRank
      ? 'upgrade'
      : planRank(selectedChange) < currentRank
        ? 'downgrade'
        : 'upgrade'
    : null;

  const extend = async () => {
    setSaving(true);
    try {
      await adminApi.extendSubscription(org.id, {
        planId: planId || undefined,
        ...(mode === 'months'
          ? { addMonths: Math.max(1, Math.floor(Number(addMonths) || 1)) }
          : { billingTerm }),
      });
      await onUpdated();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not extend subscription.');
    } finally {
      setSaving(false);
    }
  };

  const applyPlanChange = async (action: 'upgrade' | 'downgrade') => {
    if (!changePlanId) {
      window.alert('Select a plan first.');
      return;
    }
    const target = plans.find((p) => p.planId === changePlanId);
    if (!target) return;
    const label = action === 'upgrade' ? 'Upgrade' : 'Downgrade';
    if (
      !window.confirm(
        `${label} "${org.name}" from ${planName} → ${target.name}?\n\nExpiry date stays the same. SMS reminders follow the new plan entitlements.`
      )
    ) {
      return;
    }
    setChangingPlan(true);
    try {
      await adminApi.updateOrg(org.id, { planId: changePlanId });
      setChangePlanId('');
      await onUpdated();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : `Could not ${action} plan.`);
    } finally {
      setChangingPlan(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Subscription</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900">
              {planName}
              <span className="ml-2 text-xs font-medium text-zinc-500">({org.subscriptionStatus})</span>
            </p>
            <p className="mt-1 text-xs text-zinc-600">
              Billing cycle: <span className="font-medium text-zinc-800">{termLabel}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs">
            <CalendarClock className="h-4 w-4 text-emerald-700" />
            <div>
              <p className="font-semibold text-zinc-900">Plan ends</p>
              <p className="text-zinc-600">{formatExpiry(org.subscriptionExpiresAt)}</p>
              <p className={`font-medium ${expiryTone}`}>{expiryLabel(org.subscriptionExpiresAt)}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-emerald-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Change plan</p>
          <p className="mt-1 text-xs text-zinc-600">
            Upgrade or downgrade this shop&apos;s tier. Does not change the end date — use Extend below to add time.
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-medium text-zinc-600">
              New plan
              <select
                value={changePlanId}
                onChange={(e) => setChangePlanId(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-zinc-300 bg-white px-2 text-sm"
              >
                <option value="">Select plan…</option>
                {otherPlans.map((p) => {
                  const price = getPricingOptions(p)[0]?.priceInr;
                  const rank = planRank(p);
                  const tag = rank > currentRank ? 'Upgrade' : rank < currentRank ? 'Downgrade' : 'Same tier';
                  return (
                    <option key={p.planId} value={p.planId}>
                      {p.name}
                      {price != null ? ` · ${formatPlanPrice(price)}` : ''} · {tag}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="flex flex-wrap items-end gap-2">
              <button
                type="button"
                disabled={changingPlan || !changePlanId || changeAction !== 'upgrade'}
                onClick={() => void applyPlanChange('upgrade')}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowUp className="h-3.5 w-3.5" />
                {changingPlan && changeAction === 'upgrade' ? 'Saving…' : 'Upgrade plan'}
              </button>
              <button
                type="button"
                disabled={changingPlan || !changePlanId || changeAction !== 'downgrade'}
                onClick={() => void applyPlanChange('downgrade')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowDown className="h-3.5 w-3.5" />
                {changingPlan && changeAction === 'downgrade' ? 'Saving…' : 'Downgrade plan'}
              </button>
            </div>
          </div>
          {selectedChange && changeAction ? (
            <p className="mt-2 text-[11px] text-zinc-500">
              Selected: <strong>{selectedChange.name}</strong> — this is a{' '}
              <strong>{changeAction === 'upgrade' ? 'higher' : 'lower'}</strong> tier than {planName}.
            </p>
          ) : null}
        </div>

        <div className="mt-4 border-t border-emerald-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Extend plan</p>
          <p className="mt-1 text-xs text-zinc-600">Adds time from the current end date (or from today if already expired).</p>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMode('months')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                mode === 'months' ? 'bg-emerald-700 text-white' : 'border border-zinc-200 bg-white text-zinc-700'
              }`}
            >
              Add months
            </button>
            <button
              type="button"
              onClick={() => setMode('term')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                mode === 'term' ? 'bg-emerald-700 text-white' : 'border border-zinc-200 bg-white text-zinc-700'
              }`}
            >
              Add billing term
            </button>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-xs font-medium text-zinc-600">
              Plan while extending (optional)
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-zinc-300 bg-white px-2 text-sm"
              >
                <option value="">Keep current</option>
                {plans.map((p) => (
                  <option key={p.planId} value={p.planId}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            {mode === 'months' ? (
              <label className="block text-xs font-medium text-zinc-600">
                Months to add
                <select
                  value={addMonths}
                  onChange={(e) => setAddMonths(e.target.value)}
                  className="mt-1 h-9 w-full rounded-lg border border-zinc-300 bg-white px-2 text-sm"
                >
                  {['1', '3', '6', '12', '24'].map((m) => (
                    <option key={m} value={m}>
                      {m} month{m === '1' ? '' : 's'}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="block text-xs font-medium text-zinc-600">
                Billing term to add
                <select
                  value={billingTerm}
                  onChange={(e) => setBillingTerm(e.target.value as BillingTerm)}
                  className="mt-1 h-9 w-full rounded-lg border border-zinc-300 bg-white px-2 text-sm"
                >
                  {(Object.keys(TERM_LABELS) as BillingTerm[]).map((t) => (
                    <option key={t} value={t}>
                      {TERM_LABELS[t]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => void extend()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
            >
              <Plus className="h-3.5 w-3.5" />
              {saving ? 'Extending…' : 'Extend subscription'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-teal-200 bg-teal-50/70 px-3 py-2.5 text-xs text-teal-950">
        <p className="font-semibold text-teal-900">SMS billing (per shop)</p>
        <p className="mt-1 leading-relaxed text-teal-900/90">
          Each finance shop is billed by how many payment-reminder SMS were <strong>successfully sent</strong> to its
          customers in the date range (not by customer count). Cost ≈ messages × ₹
          {(detail.usage.sms ?? detail.usage.whatsapp).unitCostInr ?? 0.25}/SMS. See the SMS meter below for this
          tenant&apos;s range totals.
        </p>
      </div>
    </div>
  );
}
